import { Header, renderServiceTabs, AppVersionFooter, FloatingNav, BackToTopButton, initBackToTop } from '../components';
import { setState, navigateTo } from '../store';
import { getMiceConfig } from '../api';
import { getCurrentLanguage, formatLocalDate } from '../i18n';
import { renderCarousel } from '../components/carousel';
import Litepicker from 'litepicker';
import 'litepicker/dist/plugins/mobilefriendly';

interface LocalizedString { th: string; en: string; zh: string; ru?: string; ja?: string; ko?: string; }

interface MiceCapacity {
  theater?: number;
  classroom?: number;
  uShape?: number;
  banquet?: number;
  boardroom?: number;
  cocktail?: number;
  hollowSquare?: number;
  cabaret?: number;
}

interface MiceTimeSlot {
  id: string;
  name: LocalizedString | string;
  startTime: string;
  endTime: string;
  basePrice?: number;
}

interface MicePackage {
  id: string;
  code: string;
  name: LocalizedString | string;
  description?: LocalizedString | string;
  pricePerPax?: number;
  fixedPrice?: number;
  includes?: string[];
  minPax?: number;
}

interface MiceOutletData {
  id?: string;
  name?: LocalizedString | string;
  description?: LocalizedString | string;
  enabled?: boolean;
  areaSqm?: number;
  ceilingHeightMeters?: number;
  dimension?: string;
  floorLevel?: string;
  bookingMode?: 'instant' | 'rfp' | 'hybrid';
  capacities?: MiceCapacity;
  timeSlots?: MiceTimeSlot[];
  packages?: MicePackage[];
  amenities?: Array<{ id: string; name: LocalizedString | string; isComplimentary?: boolean }>;
  images?: Array<{ url: string; caption?: string }>;
  outlets?: MiceOutletData[];
}

let miceOutlets: MiceOutletData[] = [];
let activeOutletId: string | null = null;
let miceConfigLoaded = false;

// Selected options
let selectedLayout = 'theater';
let selectedDateStr = formatLocalDate();
let selectedSlotId = 'full_day';
let selectedPackageId = '';
let expectedPaxCount = 20;
let companyNameInput = '';
let eventTypeInput = 'meeting';
let specialNotesInput = '';

function getLocalizedText(val: any, lang: string, fallback: string = ''): string {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return val[lang] || val.en || val.th || val.zh || val.ru || val.ja || val.ko || fallback;
  }
  return fallback;
}

const LAYOUT_META: Record<string, { labelTh: string; labelEn: string; labelZh: string; labelRu?: string; labelJa?: string; labelKo?: string; icon: string }> = {
  theater: { labelTh: 'เธียเตอร์ (Theater)', labelEn: 'Theater', labelZh: '剧院式', labelRu: 'Театр', labelJa: 'シアター形式', labelKo: '극장식', icon: '🎭' },
  classroom: { labelTh: 'ห้องเรียน (Classroom)', labelEn: 'Classroom', labelZh: '课桌式', labelRu: 'Класс', labelJa: 'スクール形式', labelKo: '교실식', icon: '🏫' },
  uShape: { labelTh: 'ตัวยู (U-Shape)', labelEn: 'U-Shape', labelZh: 'U型', labelRu: 'U-образная', labelJa: 'コ字形式 (U-Shape)', labelKo: 'U자형', icon: '🧲' },
  banquet: { labelTh: 'โต๊ะกลม (Banquet)', labelEn: 'Banquet', labelZh: '宴会圆桌', labelRu: 'Банкет', labelJa: '宴会形式 (Banquet)', labelKo: '연회식', icon: '🍽️' },
  boardroom: { labelTh: 'บอร์ดรูม (Boardroom)', labelEn: 'Boardroom', labelZh: '董事会议', labelRu: 'Круглый стол', labelJa: '役員会議形式', labelKo: '이사회 회의식', icon: '💼' },
  cocktail: { labelTh: 'ค็อกเทล (Cocktail)', labelEn: 'Cocktail', labelZh: '鸡尾酒会', labelRu: 'Коктейль', labelJa: 'カクテル形式', labelKo: '칵테일식', icon: '🍸' },
  hollowSquare: { labelTh: 'สี่เหลี่ยม (Hollow Square)', labelEn: 'Hollow Square', labelZh: '回型', labelRu: 'Квадрат', labelJa: 'ロ字形式', labelKo: '중공 사각형', icon: '⏹️' },
  cabaret: { labelTh: 'คาบาเรต์ (Cabaret)', labelEn: 'Cabaret', labelZh: '小宴会式', labelRu: 'Кабаре', labelJa: 'キャバレー形式', labelKo: '카바레식', icon: '🎪' },
};

export function renderMicePage(): string {
  const lang = getCurrentLanguage();

  const labels: Record<string, {
    pageTitle: string;
    selectRoom: string;
    specs: string;
    area: string;
    ceiling: string;
    floor: string;
    capacities: string;
    packages: string;
    noPackages: string;
    eventDetails: string;
    eventDate: string;
    timeSlot: string;
    attendees: string;
    layoutStyle: string;
    company: string;
    specialReq: string;
    perPax: string;
    requestQuote: string;
    bookInstant: string;
    amenities: string;
    free: string;
    pax: string;
    sqm: string;
    meters: string;
  }> = {
    th: {
      pageTitle: 'ห้องประชุม & จัดเลี้ยง',
      selectRoom: 'เลือกห้องประชุม / สถานที่',
      specs: 'ข้อมูลพื้นที่',
      area: 'ขนาดพื้นที่',
      ceiling: 'เพดานสูง',
      floor: 'ชั้น',
      capacities: 'ความจุตามรูปแบบการจัดโต๊ะ',
      packages: 'แพ็กเกจการจัดงาน & อาหาร',
      noPackages: 'ไม่มีแพ็กเกจเสริม (เสนอราคาตามสเปก)',
      eventDetails: 'รายละเอียดการจัดงาน',
      eventDate: 'วันที่จัดงาน',
      timeSlot: 'ช่วงเวลา / รอบการใช้งาน',
      attendees: 'จำนวนผู้เข้าร่วม (ท่าน)',
      layoutStyle: 'รูปแบบการจัดโต๊ะ',
      company: 'ชื่อบริษัท / หน่วยงาน (ถ้ามี)',
      specialReq: 'ความต้องการพิเศษ / อุปกรณ์โสตทัศน์',
      perPax: '/ ท่าน',
      requestQuote: '📋 ขอใบเสนอราคา (Request Quotation)',
      bookInstant: '⚡ จองห้องประชุมทันที',
      amenities: 'อุปกรณ์และสิ่งอำนวยความสะดวก',
      free: 'ฟรี',
      pax: 'คน',
      sqm: 'ตร.ม.',
      meters: 'ม.',
    },
    en: {
      pageTitle: 'Meetings & Events (MICE)',
      selectRoom: 'Select Function Room / Space',
      specs: 'Room Specifications',
      area: 'Area',
      ceiling: 'Ceiling Height',
      floor: 'Floor',
      capacities: 'Seating Capacities',
      packages: 'Meeting Packages & Catering',
      noPackages: 'Custom quotation available upon request',
      eventDetails: 'Event Details',
      eventDate: 'Event Date',
      timeSlot: 'Session / Time Slot',
      attendees: 'Expected Attendees (Pax)',
      layoutStyle: 'Seating Layout',
      company: 'Company / Organization (Optional)',
      specialReq: 'Special Requirements / AV Requests',
      perPax: '/ Person',
      requestQuote: '📋 Request Quotation (RFP)',
      bookInstant: '⚡ Book Space Now',
      amenities: 'Complimentary Amenities & AV',
      free: 'Included',
      pax: 'pax',
      sqm: 'sqm',
      meters: 'm',
    },
    zh: {
      pageTitle: '会议与宴会厅 (MICE)',
      selectRoom: '选择会议室 / 场地',
      specs: '场地规格',
      area: '面积',
      ceiling: '层高',
      floor: '楼层',
      capacities: '摆台容纳人数',
      packages: '会议套餐与餐饮',
      noPackages: '根据需求定制报价',
      eventDetails: '活动详情',
      eventDate: '活动日期',
      timeSlot: '使用时段',
      attendees: '预计参会人数',
      layoutStyle: '摆台形式',
      company: '公司 / 机构名称（选填）',
      specialReq: '特殊需求 / 视听设备要求',
      perPax: '/ 位',
      requestQuote: '📋 申请定制报价 (RFP)',
      bookInstant: '⚡ 立即预订场地',
      amenities: '配套设施与视听设备',
      free: '免费',
      pax: '人',
      sqm: '平方米',
      meters: '米',
    },
    ru: {
      pageTitle: 'Конференц-залы и мероприятия (MICE)',
      selectRoom: 'Выберите зал / площадку',
      specs: 'Характеристики зала',
      area: 'Площадь',
      ceiling: 'Высота потолков',
      floor: 'Этаж',
      capacities: 'Вместимость по рассадке',
      packages: 'Пакеты мероприятий и кейтеринг',
      noPackages: 'Индивидуальный расчет по запросу',
      eventDetails: 'Детали мероприятия',
      eventDate: 'Дата мероприятия',
      timeSlot: 'Время проведения',
      attendees: 'Количество участников (чел.)',
      layoutStyle: 'Тип рассадки',
      company: 'Компания / Организация (не обязательно)',
      specialReq: 'Особые пожелания / Оборудование',
      perPax: '/ чел.',
      requestQuote: '📋 Запросить коммерческое предложение',
      bookInstant: '⚡ Забронировать зал',
      amenities: 'Удобства и аудиовизуальное оборудование',
      free: 'Включено',
      pax: 'чел.',
      sqm: 'кв.м',
      meters: 'м',
    },
    ja: {
      pageTitle: '会議・宴会場 (MICE)',
      selectRoom: '会場・会議室を選択',
      specs: '会場スペック',
      area: '面積',
      ceiling: '天井高',
      floor: '階数',
      capacities: 'レイアウト別収容人数',
      packages: 'ミーティングパッケージ＆飲食',
      noPackages: 'ご要望に応じたカスタム見積もり',
      eventDetails: 'イベント詳細',
      eventDate: '開催日',
      timeSlot: 'ご利用時間帯',
      attendees: '参加人数 (名)',
      layoutStyle: 'レイアウト形式',
      company: '会社名・団体名 (任意)',
      specialReq: '特別なご要望・音響設備など',
      perPax: '/ 名',
      requestQuote: '📋 お見積りを依頼する (RFP)',
      bookInstant: '⚡ 今すぐ会場を予約',
      amenities: '備品・音響設備 (無料)',
      free: '無料',
      pax: '名',
      sqm: '㎡',
      meters: 'm',
    },
    ko: {
      pageTitle: '회의 및 연회 (MICE)',
      selectRoom: '행사장 / 미팅룸 선택',
      specs: '공간 사양',
      area: '면적',
      ceiling: '층고',
      floor: '층수',
      capacities: '배치별 수용 인원',
      packages: '미팅 패키지 & 케이터링',
      noPackages: '요청 시 맞춤 견적 제공',
      eventDetails: '행사 세부 정보',
      eventDate: '행사 날짜',
      timeSlot: '이용 시간대',
      attendees: '예상 참석 인원 (명)',
      layoutStyle: '좌석 배치 형식',
      company: '회사 / 단체명 (선택사항)',
      specialReq: '특별 요청 사항 / 시청각 장비',
      perPax: '/ 인',
      requestQuote: '📋 맞춤 견적 요청 (RFP)',
      bookInstant: '⚡ 즉시 예약',
      amenities: '편의 시설 및 시청각 장비',
      free: '무료',
      pax: '명',
      sqm: '㎡',
      meters: 'm',
    },
  };
  const currentLabels = labels[lang] || labels.en;

  const activeOutlet = miceOutlets.find((o) => o.id === activeOutletId) || miceOutlets[0] || {};
  const roomName = getLocalizedText(activeOutlet.name, lang, 'Grand Ballroom');
  const roomDesc = getLocalizedText(activeOutlet.description, lang, '');
  const images = activeOutlet.images && activeOutlet.images.length > 0
    ? activeOutlet.images.map((img) => img.url)
    : ['https://firebasestorage.googleapis.com/v0/b/revio-platform-504118-media/o/images%2Fmeeting-room-default.jpg?alt=media'];

  const capacities = activeOutlet.capacities || { theater: 100, classroom: 60, uShape: 30, banquet: 80, boardroom: 24, cocktail: 120 };
  const timeSlots = activeOutlet.timeSlots && activeOutlet.timeSlots.length > 0
    ? activeOutlet.timeSlots
    : [
        { id: 'half_day_am', name: { th: 'ครึ่งวันเช้า (08:30 - 12:00)', en: 'Half-Day Morning (08:30 - 12:00)', zh: '半天上午 (08:30 - 12:00)' }, startTime: '08:30', endTime: '12:00' },
        { id: 'half_day_pm', name: { th: 'ครึ่งวันบ่าย (13:00 - 17:00)', en: 'Half-Day Afternoon (13:00 - 17:00)', zh: '半天下午 (13:00 - 17:00)' }, startTime: '13:00', endTime: '17:00' },
        { id: 'full_day', name: { th: 'เต็มวัน (08:30 - 17:00)', en: 'Full-Day (08:30 - 17:00)', zh: '全天 (08:30 - 17:00)' }, startTime: '08:30', endTime: '17:00' },
        { id: 'evening', name: { th: 'งานเลี้ยงช่วงค่ำ (18:00 - 22:00)', en: 'Evening Banquet (18:00 - 22:00)', zh: '晚宴 (18:00 - 22:00)' }, startTime: '18:00', endTime: '22:00' },
      ];

  const packages = activeOutlet.packages || [];
  const amenities = activeOutlet.amenities || [];

  return `
    <div class="page page-mice" style="padding-bottom: 90px; background: #f8f9fa;">
      ${Header(roomName, false, true)}
      ${renderServiceTabs('mice')}

      <div class="page-content" id="mice-content" style="padding: 16px; padding-bottom: 100px;">
        <!-- Room Selector Tabs (matching Rooms, Spa, Restaurant & Room Service visual rhythm) -->
        <div id="mice-outlets-scroll" style="display:flex;gap:8px;overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:8px;margin-bottom:12px;scrollbar-width:none;-ms-overflow-style:none;">
          ${(miceOutlets.length > 0 ? miceOutlets : [{ id: activeOutletId || 'mice', name: roomName }]).map((o) => {
            const isSelected = !activeOutletId || activeOutletId === o.id || miceOutlets.length === 1;
            return `
              <button
                id="mice-outlet-tab-${o.id}"
                type="button"
                ${miceOutlets.length > 1 ? `onclick="window.selectMiceOutlet?.('${o.id}')"` : ''}
                style="flex:0 0 auto;padding:8px 16px;border-radius:20px;border:1.5px solid ${isSelected ? '#00B900' : '#e0e0e0'};background:${isSelected ? '#00B900' : '#fff'};color:${isSelected ? '#fff' : '#333'};font-size:13px;font-weight:600;white-space:nowrap;cursor:${miceOutlets.length > 1 ? 'pointer' : 'default'};transition:all 0.15s ease;"
              >
                🏛️ ${getLocalizedText(o.name, lang, roomName || 'Function Room')}
              </button>
            `;
          }).join('')}
        </div>

        <!-- Split 2-Column Layout on Desktop -->
        <div class="service-detail-layout">
          <!-- Left Column: Hero Carousel, Room Specs & Seating Layouts -->
          <div class="service-left-col">
            <!-- Hero Carousel -->
            <div style="background:#fff;border-radius:14px;overflow:hidden;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);padding:0;">
              <div style="padding:8px 8px 0 8px;">
                <div id="mice-carousel-container" class="room-images" style="position:relative;border-radius:10px;overflow:hidden;height:220px;margin:0;"></div>
              </div>
              
              <div style="padding:12px 14px 14px 14px;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
                  <h2 style="margin: 0; font-size: 18px; font-weight: 700; color: #111;">${roomName}</h2>
                  <span style="font-size: 12px; background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; padding: 3px 8px; border-radius: 12px; font-weight: 600;">
                    ${activeOutlet.floorLevel || '2nd Floor'}
                  </span>
                </div>
                ${roomDesc ? `<p style="margin: 0 0 12px; font-size: 13px; color: #666; line-height: 1.5;">${roomDesc}</p>` : ''}

                <!-- Specs Grid -->
                <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; background: #f8fafc; padding: 12px; border-radius: 12px; border: 1px solid #e2e8f0; text-align: center;">
                  <div>
                    <div style="font-size: 11px; color: #64748b;">${currentLabels.area}</div>
                    <div style="font-size: 14px; font-weight: 700; color: #0f172a;">${activeOutlet.areaSqm || 120} ${currentLabels.sqm}</div>
                  </div>
                  <div>
                    <div style="font-size: 11px; color: #64748b;">${currentLabels.ceiling}</div>
                    <div style="font-size: 14px; font-weight: 700; color: #0f172a;">${activeOutlet.ceilingHeightMeters || 3.5} ${currentLabels.meters}</div>
                  </div>
                  <div>
                    <div style="font-size: 11px; color: #64748b;">🎭 Max Pax</div>
                    <div style="font-size: 14px; font-weight: 700; color: #00B900;">${capacities.theater || capacities.cocktail || 100} ${currentLabels.pax}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Seating Layout Capacities -->
            <div style="background: #fff; padding: 16px; border-radius: 16px; box-shadow: 0 2px 10px rgba(0,0,0,0.06); margin-bottom: 16px;">
              <h3 style="margin: 0 0 12px; font-size: 15px; font-weight: 700; color: #111; display: flex; align-items: center; gap: 6px;">
                <span>📐</span> ${currentLabels.capacities}
              </h3>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                ${Object.entries(capacities)
                  .filter(([_, cap]) => Number(cap) > 0)
                  .map(([layoutKey, cap]) => {
                    const meta = LAYOUT_META[layoutKey] || { labelTh: layoutKey, labelEn: layoutKey, labelZh: layoutKey, icon: '🪑' };
                    const isSelected = selectedLayout === layoutKey;
                    const layoutLabel = lang === 'th' ? meta.labelTh
                      : lang === 'zh' ? meta.labelZh
                      : lang === 'ru' ? (meta.labelRu || meta.labelEn)
                      : lang === 'ja' ? (meta.labelJa || meta.labelEn)
                      : lang === 'ko' ? (meta.labelKo || meta.labelEn)
                      : meta.labelEn;

                    return `
                      <div
                        onclick="window.selectMiceLayout?.('${layoutKey}')"
                        style="display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; border-radius: 12px; border: 1.5px solid ${isSelected ? '#00B900' : '#e5e7eb'}; background: ${isSelected ? '#f0fdf4' : '#fff'}; cursor: pointer; transition: all 0.2s;"
                      >
                        <div style="display: flex; align-items: center; gap: 8px;">
                          <span style="font-size: 16px;">${meta.icon}</span>
                          <span style="font-size: 12px; font-weight: ${isSelected ? '600' : '500'}; color: ${isSelected ? '#15803d' : '#374151'};">${layoutLabel}</span>
                        </div>
                        <span style="font-size: 13px; font-weight: 700; color: ${isSelected ? '#00B900' : '#111'};">${cap} ${currentLabels.pax}</span>
                      </div>
                    `;
                  }).join('')}
              </div>
            </div>

            <!-- Amenities Included -->
            ${amenities.length > 0 ? `
              <div style="background: #fff; padding: 16px; border-radius: 16px; box-shadow: 0 2px 10px rgba(0,0,0,0.06); margin-bottom: 20px;">
                <h3 style="margin: 0 0 10px; font-size: 14px; font-weight: 700; color: #111;">${currentLabels.amenities}</h3>
                <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                  ${amenities.map((a) => `
                    <div style="display: flex; align-items: center; gap: 6px; font-size: 12px; background: #f8fafc; border: 1px solid #e2e8f0; padding: 6px 10px; border-radius: 8px; color: #334155;">
                      <span>✓</span>
                      <span>${getLocalizedText(a.name, lang, 'Amenity')}</span>
                    </div>
                  `).join('')}
                </div>
              </div>
            ` : ''}
          </div>

          <!-- Right Column: Meeting Packages & Inquiry Form (Sticky on Desktop) -->
          <div class="service-right-col">
            <!-- Meeting Packages (Optional Selection) -->
            ${packages.length > 0 ? `
              <div style="background: #fff; padding: 16px; border-radius: 16px; box-shadow: 0 2px 10px rgba(0,0,0,0.06); margin-bottom: 16px;">
                <h3 style="margin: 0 0 12px; font-size: 15px; font-weight: 700; color: #111; display: flex; align-items: center; gap: 6px;">
                  <span>☕</span> ${currentLabels.packages}
                </h3>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  ${packages.map((pkg) => {
                    const pkgName = getLocalizedText(pkg.name, lang, 'Package');
                    const pkgDesc = getLocalizedText(pkg.description, lang, '');
                    const isSelected = selectedPackageId === pkg.id;

                    return `
                      <div
                        onclick="window.selectMicePackage?.('${pkg.id}')"
                        style="padding: 12px; border-radius: 12px; border: 1.5px solid ${isSelected ? '#00B900' : '#e5e7eb'}; background: ${isSelected ? '#f0fdf4' : '#fff'}; cursor: pointer;"
                      >
                        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px;">
                          <div style="font-size: 14px; font-weight: 700; color: #111;">${pkgName}</div>
                          <div style="font-size: 14px; font-weight: 800; color: #00B900;">
                            ${pkg.pricePerPax ? `฿${pkg.pricePerPax.toLocaleString()} <span style="font-size: 11px; font-weight: 400; color: #666;">${currentLabels.perPax}</span>` : 'Custom'}
                          </div>
                        </div>
                        ${pkgDesc ? `<div style="font-size: 12px; color: #666; margin-bottom: 6px;">${pkgDesc}</div>` : ''}
                        ${pkg.includes && pkg.includes.length > 0 ? `
                          <div style="display: flex; flex-wrap: wrap; gap: 4px;">
                            ${pkg.includes.map((inc) => `<span style="font-size: 10px; background: #e2e8f0; color: #475569; padding: 2px 6px; border-radius: 6px;">✓ ${inc}</span>`).join('')}
                          </div>
                        ` : ''}
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            ` : ''}

            <!-- Booking / Inquiry Form -->
            <div style="background: #fff; padding: 16px; border-radius: 16px; box-shadow: 0 2px 10px rgba(0,0,0,0.06); margin-bottom: 16px;">
              <h3 style="margin: 0 0 14px; font-size: 15px; font-weight: 700; color: #111; display: flex; align-items: center; gap: 6px;">
                <span>📋</span> ${currentLabels.eventDetails}
              </h3>

              <!-- Date Picker -->
              <div style="margin-bottom: 12px;">
                <label style="display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;">${currentLabels.eventDate}</label>
                <input
                  id="mice-datepicker"
                  type="text"
                  readonly
                  value="${selectedDateStr}"
                  style="width: 100%; box-sizing: border-box; height: 42px; padding: 0 12px; border: 1px solid #cbd5e1; border-radius: 10px; font-size: 14px; background: #fff;"
                />
              </div>

              <!-- Time Session Slots -->
              <div style="margin-bottom: 12px;">
                <label style="display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;">${currentLabels.timeSlot}</label>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                  ${timeSlots.map((ts) => {
                    const isSelected = selectedSlotId === ts.id;
                    const slotTitle = getLocalizedText(ts.name, lang, `${ts.startTime} - ${ts.endTime}`);
                    return `
                      <button
                        type="button"
                        onclick="window.selectMiceSlot?.('${ts.id}')"
                        style="padding: 10px 8px; border-radius: 10px; border: 1.5px solid ${isSelected ? '#00B900' : '#cbd5e1'}; background: ${isSelected ? '#f0fdf4' : '#fff'}; color: ${isSelected ? '#15803d' : '#334155'}; font-size: 12px; font-weight: ${isSelected ? '700' : '500'}; text-align: center; cursor: pointer;"
                      >
                        ${slotTitle}
                      </button>
                    `;
                  }).join('')}
                </div>
              </div>

              <!-- Expected Pax Stepper -->
              <div style="margin-bottom: 12px;">
                <label style="display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;">${currentLabels.attendees}</label>
                <div style="display: flex; align-items: center; gap: 12px;">
                  <button
                    type="button"
                    onclick="window.adjustMicePax?.(-5)"
                    style="width: 40px; height: 40px; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 18px; font-weight: bold; cursor: pointer;"
                  >-</button>
                  <input
                    id="mice-pax-input"
                    type="number"
                    value="${expectedPaxCount}"
                    onchange="window.setMicePaxDirect?.(this.value)"
                    style="flex: 1; height: 40px; text-align: center; border: 1px solid #cbd5e1; border-radius: 10px; font-size: 16px; font-weight: 700;"
                  />
                  <button
                    type="button"
                    onclick="window.adjustMicePax?.(5)"
                    style="width: 40px; height: 40px; border-radius: 10px; border: 1px solid #cbd5e1; background: #f8fafc; font-size: 18px; font-weight: bold; cursor: pointer;"
                  >+</button>
                </div>
              </div>

              <!-- Company / Organization -->
              <div style="margin-bottom: 12px;">
                <label style="display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;">${currentLabels.company}</label>
                <input
                  id="mice-company-input"
                  type="text"
                  value="${companyNameInput}"
                  placeholder="e.g. Acme Corp Ltd."
                  oninput="window.setMiceCompany?.(this.value)"
                  style="width: 100%; box-sizing: border-box; height: 42px; padding: 0 12px; border: 1px solid #cbd5e1; border-radius: 10px; font-size: 14px; background: #fff;"
                />
              </div>

              <!-- Special Notes / AV Requirements -->
              <div style="margin-bottom: 16px;">
                <label style="display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;">${currentLabels.specialReq}</label>
                <textarea
                  id="mice-notes-input"
                  rows="2"
                  placeholder="e.g. Need 4 wireless microphones, Zoom conference setup, vegetarian lunch..."
                  oninput="window.setMiceNotes?.(this.value)"
                  style="width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 10px; font-size: 13px; background: #fff;"
                >${specialNotesInput}</textarea>
              </div>

              <!-- Submit Button -->
              <button
                onclick="window.submitMiceBooking?.()"
                style="width: 100%; height: 50px; border: none; border-radius: 14px; background: #00B900; color: #fff; font-size: 16px; font-weight: 700; box-shadow: 0 4px 14px rgba(0,185,0,0.35); cursor: pointer; transition: transform 0.15s ease;"
              >
                ${currentLabels.requestQuote}
              </button>
            </div>
          </div>
        </div>
      </div>


      <!-- Version Footer -->
      ${AppVersionFooter()}

    </div>

    ${BackToTopButton('mice')}

    ${FloatingNav()}
  `;
}

export function initMicePage(): void {
  // Render Hero Carousel
  const carouselEl = document.getElementById('mice-carousel-container');
  if (carouselEl) {
    const activeOutlet = miceOutlets.find((o) => o.id === activeOutletId) || miceOutlets[0];
    const outletImages = (activeOutlet?.images && activeOutlet.images.length > 0)
      ? activeOutlet.images.map((img: any) => ({ url: typeof img === 'string' ? img : (img.url || img.thumbUrl) }))
      : [{ url: 'https://firebasestorage.googleapis.com/v0/b/revio-platform-504118-media/o/images%2Fmeeting-room-default.jpg?alt=media' }];
    renderCarousel(carouselEl, outletImages);
  }

  // Load MICE Config if not loaded
  if (!miceConfigLoaded) {
    getMiceConfig().then((data) => {
      if (data && data.outlets && data.outlets.length > 0) {
        miceOutlets = data.outlets;
        if (!activeOutletId) activeOutletId = data.outlets[0].id;
      } else if (data && data.id) {
        miceOutlets = [data as any];
        activeOutletId = data.id;
      }
      miceConfigLoaded = true;
      (window as any).renderApp?.();
    }).catch(() => {});
  }

  // Setup Litepicker
  const dateEl = document.getElementById('mice-datepicker');
  if (dateEl) {
    new Litepicker({
      element: dateEl,
      singleMode: true,
      format: 'YYYY-MM-DD',
      minDate: new Date(),
      setup: (picker: any) => {
        picker.on('selected', (date: any) => {
          selectedDateStr = date.format('YYYY-MM-DD');
          (window as any).renderApp?.();
        });
      },
    });
  }

  // Window helper handlers
  (window as any).selectMiceOutlet = (id: string) => {
    activeOutletId = id;
    (window as any).renderApp?.();
  };

  (window as any).selectMiceLayout = (layout: string) => {
    selectedLayout = layout;
    (window as any).renderApp?.();
  };

  (window as any).selectMiceSlot = (slotId: string) => {
    selectedSlotId = slotId;
    (window as any).renderApp?.();
  };

  (window as any).selectMicePackage = (pkgId: string) => {
    selectedPackageId = selectedPackageId === pkgId ? '' : pkgId;
    (window as any).renderApp?.();
  };

  (window as any).adjustMicePax = (delta: number) => {
    expectedPaxCount = Math.max(5, expectedPaxCount + delta);
    (window as any).renderApp?.();
  };

  (window as any).setMicePaxDirect = (val: any) => {
    expectedPaxCount = Math.max(1, Number(val) || 10);
    (window as any).renderApp?.();
  };

  (window as any).setMiceCompany = (val: string) => {
    companyNameInput = val;
  };

  (window as any).setMiceNotes = (val: string) => {
    specialNotesInput = val;
  };

  (window as any).submitMiceBooking = () => {
    const lang = getCurrentLanguage();
    const activeOutlet = miceOutlets.find((o) => o.id === activeOutletId) || miceOutlets[0] || {};
    const functionRoomName = getLocalizedText(activeOutlet.name, lang, 'Function Room');

    const selectedPkg = (activeOutlet.packages || []).find((p) => p.id === selectedPackageId);
    const selectedTimeSlotObj = (activeOutlet.timeSlots || []).find((ts) => ts.id === selectedSlotId);
    const timeSlotName = selectedTimeSlotObj ? getLocalizedText(selectedTimeSlotObj.name, lang, 'Full-Day') : '08:30 - 17:00';

    const pricePerPax = Number(selectedPkg?.pricePerPax || 0);
    const estTotalAmount = pricePerPax > 0 ? pricePerPax * expectedPaxCount : 0;

    const miceDetails = {
      functionRoomId: activeOutlet.id || 'main-ballroom',
      functionRoomName,
      eventType: eventTypeInput,
      layoutStyle: selectedLayout,
      eventDate: selectedDateStr,
      timeSlotId: selectedSlotId,
      timeSlotName,
      expectedPax: expectedPaxCount,
      packageId: selectedPackageId || undefined,
      packageName: selectedPkg ? getLocalizedText(selectedPkg.name, lang, '') : undefined,
      companyName: companyNameInput,
      specialRequests: specialNotesInput,
      isRfpInquiry: true,
    };

    setState({
      bookingType: 'MICE',
      miceDetails,
      selectedRoom: {
        roomTypeCode: activeOutlet.id || 'MICE_ROOM',
        roomTypeName: functionRoomName,
        ratePlanCode: selectedPackageId || 'MICE_PACKAGE',
        ratePlanName: selectedPkg ? getLocalizedText(selectedPkg.name, lang, 'Meeting Package') : 'Custom Quotation',
        totalRate: estTotalAmount,
        ratePerRoom: estTotalAmount,
        averageNightlyRate: estTotalAmount,
        totalBeforeTax: estTotalAmount,
        numberOfRooms: 1,
        currency: 'THB',
        available: 1,
      },
    });

    navigateTo('guest-info');
  };

  window.handleForward = () => {
    (window as any).submitMiceBooking?.();
  };

  window.handleBack = () => {
    navigateTo('search');
  };

  // Floating "BACK TO TOP" for MICE
  initBackToTop('mice');
}
